import { StCeloAccountWithdrawalAbi } from '../abis/stcelo'
import type { LstWithdrawalRequest } from '../types'
import {
  computeStatus,
  toBigInt,
  toNumber,
  type LstWithdrawalReader,
} from './shared'

/**
 * StakedCelo (stCELO) — pending-withdrawal enumeration.
 *
 * Single stage: `Account.getPendingWithdrawals(beneficiary)` returns
 * parallel `(values[], timestamps[])` arrays. Each entry is a
 * LockedGold unlock created by `Account.withdraw`; `timestamp` is the
 * unix-second moment it becomes claimable via
 * `Account.finishPendingWithdrawal`.
 *
 * Entries only appear here **after** the per-group `Account.withdraw`
 * step has run (the post-`Manager.withdraw` scheduled withdrawals are
 * not yet LockedGold pending withdrawals and so are not enumerated).
 */
export const readerStCelo: LstWithdrawalReader = {
  fetch: async (user, multicallRetry, chainId, entry) => {
    const account = entry.withdrawalContract
    if (!account) return []

    let res: unknown[]
    try {
      res = (await multicallRetry({
        chain: chainId,
        calls: [
          {
            address: account,
            name: 'getPendingWithdrawals',
            params: [user],
          },
        ] as unknown as never[],
        abi: [StCeloAccountWithdrawalAbi] as unknown as never,
      })) as unknown[]
    } catch {
      return []
    }

    // viem decodes the two-array return as a tuple `[values, timestamps]`.
    const cell = res[0] as [unknown[], unknown[]] | undefined
    if (!Array.isArray(cell) || !Array.isArray(cell[0])) return []
    const values = cell[0]
    const timestamps = cell[1] ?? []

    const out: LstWithdrawalRequest[] = []
    for (let i = 0; i < values.length; i++) {
      const amount = toBigInt(values[i])
      const readyAt = toNumber(timestamps[i])
      if (amount === undefined || readyAt === undefined) continue
      out.push({
        lst: entry.lst,
        brand: entry.brand,
        symbol: entry.symbol,
        requestId: String(i),
        amountUnderlying: amount.toString(),
        status: computeStatus(readyAt),
        readyAt,
      })
    }
    return out
  },
}
